import {
  CENTS_IN_DOLLAR,
  TimeInterval,
} from '~/shared/constants/commonConstants'
import { getFormattedPrice } from '~/shared/helpers/getFormattedPrice'
import { getOldPricesList } from '~/shared/helpers/getOldPricesList'
import {
  ISubscription,
  TPaypalPlanId,
  TRawPriceAmount,
} from '~/shared/types/subscriptionsTypes'

export interface IRawOneTimeSubscription {
  plans: {
    id: number
    intro_diff: {
      discount_amount: number
      discount_percent: number
    }
    paypal: { plan_id: TPaypalPlanId } | null
    paywall: {
      is_default: boolean
      show_order: number
      discount_percent: number
    }
    pricing: {
      currency: string
      trial: { duration_days: number }
    }
    product_key: string
    stripe: { intro_lookup_key: string }
    trial: {
      current_price: TRawPriceAmount
      old_prices: {
        before_custom_discount: TRawPriceAmount
        before_33_percent_discount: TRawPriceAmount
        before_50_percent_discount: TRawPriceAmount
        before_75_percent_discount: TRawPriceAmount
      }
    }
  }[]
}

export const getOneTimeSubscriptionListFromRawSubscriptionList = (
  rawSubscriptionList: IRawOneTimeSubscription,
): ISubscription[] =>
  rawSubscriptionList?.plans.map((rawSubscription) => ({
    id: rawSubscription.paywall.show_order.toString(),
    planId: rawSubscription.id,
    product: rawSubscription.product_key,
    paypalPlanId: rawSubscription.paypal?.plan_id ?? '',
    isDefault: rawSubscription.paywall.is_default,
    discountPercent: rawSubscription.paywall.discount_percent,
    currency: rawSubscription.pricing.currency.toLowerCase(),
    lookupKey: rawSubscription.stripe.intro_lookup_key,
    trialLookupKey: rawSubscription.stripe.intro_lookup_key,
    isOneTime: true,

    introDiff: {
      discountAmount: getFormattedPrice(
        rawSubscription.intro_diff?.discount_amount,
      ),
      discountPercentage: rawSubscription.intro_diff?.discount_percent,
    },

    mainPrices: {
      periodName: TimeInterval.ONCE,
      periodQuantity: 1,
      fullPrice: getFormattedPrice(rawSubscription.trial.current_price.amount),
      fullPriceTax: getFormattedPrice(
        rawSubscription.trial.current_price.tax_amount,
      ),
      discountAmount:
        rawSubscription.trial.current_price.amount_of_discount /
        CENTS_IN_DOLLAR,
      daily:
        rawSubscription.trial.current_price.in_the_box.daily / CENTS_IN_DOLLAR,
      weekly:
        rawSubscription.trial.current_price.in_the_box.weekly / CENTS_IN_DOLLAR,
      monthly:
        rawSubscription.trial.current_price.in_the_box.monthly /
        CENTS_IN_DOLLAR,
      yearly:
        rawSubscription.trial.current_price.in_the_box.yearly / CENTS_IN_DOLLAR,

      oldPrices: {
        beforeCustomDiscount:
          rawSubscription.trial?.old_prices.before_custom_discount &&
          getOldPricesList(
            rawSubscription.trial?.old_prices.before_custom_discount,
          ),
        before33PercentDiscount: getOldPricesList(
          rawSubscription.trial?.old_prices.before_33_percent_discount,
        ),
        before50PercentDiscount: getOldPricesList(
          rawSubscription.trial?.old_prices.before_50_percent_discount,
        ),
        before75PercentDiscount: getOldPricesList(
          rawSubscription.trial?.old_prices.before_75_percent_discount,
        ),
      },
    },

    trialPrices: {
      fullPrice: getFormattedPrice(rawSubscription.trial?.current_price.amount),
      fullPriceTax: getFormattedPrice(
        rawSubscription.trial?.current_price.tax_amount,
      ),
      durationDays: rawSubscription.pricing.trial?.duration_days,
      daily: getFormattedPrice(
        rawSubscription.trial?.current_price.in_the_box.daily,
      ),
      weekly: getFormattedPrice(
        rawSubscription.trial?.current_price.in_the_box.weekly,
      ),
      monthly: getFormattedPrice(
        rawSubscription.trial?.current_price.in_the_box.monthly,
      ),
      yearly: getFormattedPrice(
        rawSubscription.trial?.current_price.in_the_box.yearly,
      ),

      oldPrices: {
        beforeCustomDiscount:
          rawSubscription.trial?.old_prices.before_custom_discount &&
          getOldPricesList(
            rawSubscription.trial?.old_prices.before_custom_discount,
          ),
        before33PercentDiscount: getOldPricesList(
          rawSubscription.trial?.old_prices.before_33_percent_discount,
        ),
        before50PercentDiscount: getOldPricesList(
          rawSubscription.trial?.old_prices.before_50_percent_discount,
        ),
        before75PercentDiscount: getOldPricesList(
          rawSubscription.trial?.old_prices.before_75_percent_discount,
        ),
      },
    },
  }))
